import React, { ChangeEvent, FocusEvent, KeyboardEvent } from 'react';
import AutosizeInput from 'react-input-autosize';
import tw from 'twin.macro';
import styled from '@emotion/styled';
import { ExclamationTriangleIcon as Exclamation } from '@heroicons/react/20/solid';

export interface Props {
  id?: string;
  value: string;
  autoSize?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  hasError?: boolean;
  type?: string;
  icon?: any;
  rightElement?: any;
  small?: boolean;
  large?: boolean;
  selectAllOnFocus?: boolean;
  extraLarge?: boolean;
  hasLabel?: boolean;
  className?: string;
  autoFocus?: boolean;
  placeholder?: string;
  autoComplete?: 'on' | 'off';
  inputRef?: any;
  withoutMargin?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  fill?: boolean;
  style?: object;
  tabIndex?: number;
  onClick?: any;
  'aria-label'?: string;
  step?: string;
  max?: number;
  min?: number;
  enterKeyHint?: 'enter' | 'done' | 'go' | 'next' | 'previous' | 'send';
  onKeyPress?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  required?: boolean;
  name?: string;
}

interface InputStyleProps {
  rightElement?: any;
  error?: boolean;
  icon?: boolean;
  small?: boolean;
  large?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  extraLarge?: boolean;
}

export const inputBorderStyle = (p: {
  error?: boolean;
  disabled?: boolean;
  readonly?: boolean;
}) => [
  tw`border border-neutral-quiet rounded-md text-neutral-moderate placeholder:text-neutral-quiet placeholder:opacity-70`,
  !(p.disabled || p.readonly) && !p.error && tw`hover:border-brand-intense`,
  !p.error &&
    tw`focus-within:ring-[3px] focus-within:ring-brand-moderate focus-within:border-brand-intense`,
  p.error &&
    tw`focus-within:ring-danger-intense focus-within:border-danger-intense border-danger-intense`,
];

const inputHeight = (p: {
  small?: boolean;
  large?: boolean;
  extraLarge?: boolean;
  icon?: boolean;
}) => {
  if (p.small) {
    return [tw`h-6!`, p.icon && tw`pl-6!`];
  }
  if (p.large) {
    return [tw`h-10`, p.icon && tw`pl-9!`];
  }
  if (p.extraLarge) {
    return [tw`h-14`, p.icon && tw`pl-14!`];
  }
  return [tw`h-9`, p.icon && tw`pl-9!`];
};

export const inputRootStyle = (p: InputStyleProps) => [
  inputBorderStyle({
    error: p.error,
    disabled: p.disabled,
    readonly: p.readonly,
  }),
  inputHeight({
    small: p.small,
    large: p.large,
    extraLarge: p.extraLarge,
    icon: p.icon,
  }),
  tw`block w-full text-sm py-1 px-2`,
  tw`leading-normal`,
  p.large && tw`sm:text-base`,
  p.small && tw`p-0!`,
  p.disabled && tw`bg-neutral-quiet text-neutral-supershy`,
  p.readonly && tw`bg-neutral-quiet text-neutral-moderate`,
  p.extraLarge && tw`text-xl sm:text-3xl`,
  p.error && tw`pr-8`,
  p.rightElement && tw`pr-8!`,
];

const StyledInput: any = styled(
  ({
    type,
    id,
    placeholder,
    value,
    inputRef,
    icon,
    error,
    small,
    large,
    autoSize,
    extraLarge,
    rightElement,
    selectAllOnFocus,
    onFocus,
    readonly,
    ...rest
  }) => {
    const props = {
      type: type || 'text',
      ref: inputRef,
      id: id,
      placeholder: placeholder,
      readOnly: readonly,
      value,
      onFocus: e => {
        if (selectAllOnFocus) {
          e.target.select();
        }
        if (onFocus) {
          onFocus(e);
        }
      },
      ...rest,
    };
    if (autoSize) {
      return (
        <AutosizeInput
          {...props}
          disabled={rest.disabled || readonly}
          inputClassName={(props as any).className}
          className=""
        />
      );
    }
    return <input {...props} disabled={rest.disabled || readonly} />;
  },
)((p: InputStyleProps) => [inputRootStyle(p)]);

const Wrapper = styled.div(
  ({ hasLabel, shouldFill }: { hasLabel?: boolean; shouldFill?: boolean }) => [
    tw`relative`,
    hasLabel && tw`mt-1`,
    shouldFill && tw`w-full`,
  ],
);

export default function Input(props: Props) {
  const {
    hasError,
    type,
    icon,
    rightElement,
    id,
    autoFocus = false,
    className = '',
    small = false,
    large = false,
    placeholder,
    inputRef,
    onChange,
    extraLarge = false,
    selectAllOnFocus = false,
    onBlur,
    autoComplete,
    value,
    hasLabel,
    fill,
    ...rest
  } = props;
  return (
    <Wrapper hasLabel={hasLabel} shouldFill={fill}>
      {icon && (
        <div
          css={[
            tw`pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center text-neutral-moderate`,
            hasError && tw`text-danger-intense`,
            extraLarge && tw`pl-4`,
          ]}
        >
          <span css={[tw`h-5 w-5`, extraLarge && tw`h-6 w-6`]}>
            {React.isValidElement(icon) ? icon : React.createElement(icon)}
          </span>
        </div>
      )}
      <StyledInput
        id={id}
        error={hasError}
        inputRef={inputRef}
        type={type}
        autoFocus={autoFocus}
        placeholder={placeholder}
        large={large}
        small={small}
        icon={!!icon}
        selectAllOnFocus={selectAllOnFocus}
        className={className}
        onChange={onChange}
        extraLarge={extraLarge}
        value={value}
        onBlur={onBlur}
        rightElement={!!rightElement}
        autoComplete={autoComplete}
        {...rest}
      />
      {rightElement && (
        <div tw="absolute top-0 bottom-0 right-1 pr-0.5 flex items-center">
          {rightElement}
        </div>
      )}
      {hasError && (
        <div tw="absolute top-0 bottom-0 right-0 pr-2 flex items-center pointer-events-none">
          <Exclamation tw="h-5 w-5 text-danger-moderate" aria-hidden="true" />
        </div>
      )}
    </Wrapper>
  );
}
